import { logToConsole, sendNewRelicCustomEvent } from '@utils/logging/logging-utils';

export const convertStringToBuffer = (str: string): Uint8Array => Uint8Array.from(str, (c) => c.charCodeAt(0));

export const convertBufferToString = (buffer: Uint8Array): string => String.fromCharCode(...new Uint8Array(buffer));

export const importKey = (keyBuffer: BufferSource): Promise<CryptoKey | void> =>
  crypto.subtle
    .importKey(
      'spki',
      keyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt'],
    )
    .catch((error) => {
      logToConsole(error);
      sendNewRelicCustomEvent('encryptionFailure', {
        error,
        errorIdentifier: 'importKeyError',
      });
      throw new Error(error);
    });

export const encryptValues = (importedKey: CryptoKey, valueBuffer: BufferSource): Promise<ArrayBuffer | void> =>
  crypto.subtle
    .encrypt(
      {
        name: 'RSA-OAEP',
      },
      importedKey,
      valueBuffer,
    )
    .catch((error) => {
      logToConsole(error);
      sendNewRelicCustomEvent('encryptionFailure', {
        error,
        errorIdentifier: 'encryptValuesError',
      });
      throw new Error(error);
    });

export interface EncryptFunctionInput {
  publicKey: string;
  value: string;
}

export type EncryptFunction = () => Promise<string | undefined>;

export const encrypt =
  (input: EncryptFunctionInput): EncryptFunction =>
    async () => {
      const { publicKey, value } = input;
      try {
        // publicKey is base64 encoded so need to decode for use
        const binaryPublicKey: string = atob(publicKey);

        // key string needs to be an arraybuffer to use in CryptoKey creation
        const keyBuffer: Uint8Array = convertStringToBuffer(binaryPublicKey);

        // create a CryptoKey to use with .encrypt
        const importedKey: CryptoKey | void = await importKey(keyBuffer);

        // convert the provided payload values into an arraybuffer for use during encryption
        const valueBuffer: Uint8Array = convertStringToBuffer(value);

        // encrypt with our crypto key and value buffer
        const encryptedValueBuffer: void | ArrayBuffer = await encryptValues(importedKey as CryptoKey, valueBuffer);

        // turn the encrypted arraybuffer result into an encrypted binary string
        const encryptedBinaryString: string = convertBufferToString(encryptedValueBuffer as Uint8Array);

        // final step is to base64 encode the payload so it can be sent to the API
        return btoa(encryptedBinaryString);
      } catch (error) {
        logToConsole(error);
        throw new Error('Encryption failed');
      }
    };

export default encrypt;
